import { Feature, FeatureCollection } from 'geojson';

export const isDescriptionItem = (
  item: Content.Properties.TripRouteDetailsItem,
): item is Content.Properties.DescriptionItem => {
  return item.$type === 'DescriptionItemModuleDto';
};

export const isDescriptionAndTransportList = (
  item: Content.Properties.TripRouteDetailsItem,
): item is Content.Properties.DescriptionAndTransportList => {
  return item.$type === 'DescriptionAndTransportListDto';
};

export const isLandmarkItem = (
  item: Content.Properties.TripRouteDetailsItem,
): item is Content.Properties.LandmarkItem => {
  return item.$type === 'LandmarkItemModuleDto';
};

export const isTransportModeItem = (
  item: Content.Properties.TripRouteDetailsItem,
): item is Content.Properties.TransportModeItem => {
  return item.$type === 'TransportModeItemDto';
};

/**
 * Map marker items
 */
export const extractDescriptionItems = (
  items: Content.Properties.TripRouteDetailsItem[],
): Content.Properties.DescriptionItem[] => {
  let result: Content.Properties.DescriptionItem[] = [];

  for (const item of items) {
    if (isDescriptionItem(item)) {
      result.push(item);
    }

    if (
      item.$type === 'DescriptionAndTransportListDto' &&
      'nestedItems' in item
    ) {
      result = result.concat(extractDescriptionItems(item.nestedItems));
    }
  }

  return result.filter(item => !!item.pointOfInterest);
};

export const mapMarkersFeatureAdapter = (
  item: Content.Properties.DescriptionItem,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.pointOfInterest?.longitude ?? 0,
        item.pointOfInterest?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      id: String(item.pointOfInterest?.id),
      markerLabel: item.pointOfInterest?.name,
      name: item.pointOfInterest?.name,
      type: item.pointOfInterest?.icon,
    },
    type: 'Feature',
  };
};

export const mapMarkersFeatureCollectionAdapter = (
  items?: Content.Properties.DescriptionItem[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item => mapMarkersFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};

/**
 * Map landmark items
 */
export const extractLandmarkItems = (
  items: Content.Properties.TripRouteDetailsItem[],
): Content.Properties.LandmarkItem[] => {
  const result: Content.Properties.LandmarkItem[] = [];

  for (const item of items) {
    if (isLandmarkItem(item)) {
      result.push(item);
    }
  }

  return result;
};

export const mapLandmarksFeatureAdapter = (
  item: Content.Properties.LandmarkItem,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.pointOfInterest?.longitude ?? 0,
        item.pointOfInterest?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      id: String(item.pointOfInterest?.id),
      landmarkLabel: item.pointOfInterest?.name,
      name: item.pointOfInterest?.name,
      type: item.pointOfInterest?.icon,
    },
    type: 'Feature',
  };
};

export const mapLandmarksFeatureCollectionAdapter = (
  items?: Content.Properties.LandmarkItem[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item => mapLandmarksFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};

/**
 * Transport mode items
 */
export const extractTransportModeItems = (
  items: Content.Properties.TripRouteDetailsItem[],
): Content.Properties.TransportModeItem[] => {
  let result: Content.Properties.TransportModeItem[] = [];

  for (const item of items) {
    if (isTransportModeItem(item)) {
      result.push(item);
    }

    if (
      item.$type === 'DescriptionAndTransportListDto' &&
      'nestedItems' in item
    ) {
      result = result.concat(extractTransportModeItems(item.nestedItems));
    }
  }

  return result;
};

export const transportModesFeatureAdapter = (
  item: Content.Properties.TransportModeItem,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.geoLocation?.longitude ?? 0,
        item.geoLocation?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      headline: item.headline,
      icon: item.transportMode,
      text: item.text,
    },
    type: 'Feature',
  };
};

export const transportModesFeatureCollectionAdapter = (
  items?: Content.Properties.TransportModeItem[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items
      .filter(item => item.geoLocation)
      .map(item => transportModesFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};

/**
 * Utilities
 */
export const getMapMarkerIndex = (
  items: Content.Properties.TripRouteDetailsItem[],
  id: string,
): number => {
  return items.findIndex(item => {
    if (isDescriptionItem(item)) {
      return item.pointOfInterest?.id === id;
    }

    if (isDescriptionAndTransportList(item)) {
      return item.nestedItems.some(
        nestedItem =>
          isDescriptionItem(nestedItem) &&
          nestedItem.pointOfInterest?.id === id,
      );
    }

    return false;
  });
};
